/**
 * L0 recompose contract for the Vault PWA (WR J-3704).
 *
 * Every lane builds against the types in this file, and this file was written before any lane
 * code. It holds three seams:
 *
 * 1. `VaultDataSource` is the only way data enters the app. J-3704 ships `FixtureDataSource`
 *    (fake data). J-3705 adds the real read-only replica behind the same interface (03
 *    Option A). The interface is READ-ONLY on purpose: there is no write method, so no screen
 *    can grow a write path by accident.
 * 2. `Unlocker` is the only way the lock opens. J-3704 ships a no-op placeholder. The real
 *    WebAuthn-PRF unlocker (03 §F-2) replaces it later. There is no password field and no
 *    confirm-dialog fallback anywhere (03 SI-12, "fail closed").
 *    The two seams are bound together (CSO review of PR #95): a successful unlock returns an
 *    `UnlockSession`, and `VaultDataSource.load` takes it. A data source refuses a session that
 *    was not issued by an unlocker or that a lock has since revoked (src/auth/session.ts), and
 *    the shell refuses to put the placeholder unlocker in front of anything but fixture data.
 * 3. `ClipboardPort` wraps `navigator.clipboard` so the copy-then-clear-on-next-tap state
 *    machine can be tested without a browser.
 */

// ---------------------------------------------------------------- data

export type WalletId = string;

/** One wallet. The order of `VaultSnapshot.wallets` IS the vault order (Quick Search D-8). */
export interface Wallet {
  id: WalletId;
  name: string;
}

export const CATEGORIES = [
  "login",
  "password",
  "secure-note",
  "credit-card",
  "identity",
  "api-credential",
  "database",
  "server",
] as const;
export type CategoryId = (typeof CATEGORIES)[number];

export const CATEGORY_LABEL: Record<CategoryId, string> = {
  login: "Login",
  password: "Password",
  "secure-note": "Secure Note",
  "credit-card": "Credit Card",
  identity: "Identity",
  "api-credential": "API Credential",
  database: "Database",
  server: "Server",
};

/**
 * Field kinds, matching Quick Search's DetailFields kinds (username, password, totp, url,
 * text) plus `note` for long free text. `otp` carries a seed that is NEVER rendered. The
 * detail view shows a live code in its place (Quick Search rule 7).
 */
export type FieldKind = "username" | "password" | "otp" | "url" | "text" | "note";

export interface ItemField {
  /** Unique within the item. Pinned-field references use `${itemId}#${fieldId}`. */
  id: string;
  kind: FieldKind;
  label: string;
  value: string;
  /** Masked until revealed. Unknown text is sensitive by default (Quick Search rule 7). */
  sensitive: boolean;
  /** Only for `kind: "otp"`. */
  otp?: { period: number; digits: number };
}

export interface VaultItem {
  /** Stable id, `${wallet}/${slug}`. */
  id: string;
  wallet: WalletId;
  title: string;
  category: CategoryId;
  /** Vowed-style tags (type, domain, …). The ranker scores tag keys and values. */
  tags: Record<string, string>;
  fields: ItemField[];
  favorite: boolean;
  archived: boolean;
  deleted: boolean;
  /** RFC 3339. */
  createdAt: string;
  modifiedAt: string;
  lastUsedAt?: string;
  useCount: number;
}

export interface WalletFailure {
  wallet: WalletId;
  reason: string;
}

/**
 * What a data source returns. `asOf` is when the replica was last written. The UI shows its
 * age and warns when it is stale (03 CSO caveat: show replica age). `failedWallets` enables
 * the partial index (Quick Search rule 11): one wallet failing never empties the others.
 */
export interface VaultSnapshot {
  wallets: Wallet[];
  items: VaultItem[];
  asOf: string;
  failedWallets: WalletFailure[];
  /** True for fixture data. The UI then shows a persistent FAKE DATA marker. */
  fixture: boolean;
}

export interface VaultDataSource {
  readonly kind: "fixture" | "replica";
  /** Rejects unless `session` is live: issued by an unlocker and not revoked by a lock since. */
  load(session: UnlockSession): Promise<VaultSnapshot>;
}

// ---------------------------------------------------------------- unlock

/**
 * Which unlocker is wired in. `none` is the non-fixture build until J-3705 lands WebAuthn-PRF:
 * it never opens. Shown in Settings so the reviewer can see which unlocker is live.
 */
export type UnlockMethod = "placeholder" | "webauthn-prf" | "none";

/**
 * Proof of one successful unlock, and the only thing `VaultDataSource.load` accepts. Issue and
 * revoke it through src/auth/session.ts; a look-alike object is not live. J-3705 adds the
 * replica key the WebAuthn-PRF unlock releases.
 */
export interface UnlockSession {
  readonly method: Exclude<UnlockMethod, "none">;
}

export type UnlockFailure = "cancelled" | "unavailable" | "failed";
export type UnlockResult = { ok: true; session: UnlockSession } | { ok: false; reason: UnlockFailure };

export interface Unlocker {
  readonly method: UnlockMethod;
  /**
   * Must be called from a user gesture. WebKit refuses WebAuthn outside one.
   *
   * The shell aborts `signal` when the prompt closes before its answer arrives (the page hides
   * under auto-lock "Immediately", or the vault locks). A WebAuthn unlocker passes it to
   * `navigator.credentials.get`; the placeholder ignores it. Ignoring it is still safe: the shell
   * discards any answer for a prompt that is no longer the open one, and treats a rejection as
   * "failed" (CSO gate on PR #100).
   */
  unlock(signal: AbortSignal): Promise<UnlockResult>;
}

// ---------------------------------------------------------------- clipboard + clock

export interface ClipboardPort {
  /** Must run inside a user gesture (WebKit async clipboard rule). */
  writeText(text: string): Promise<void>;
}

export interface Clock {
  now(): number;
}

export const systemClock: Clock = { now: () => Date.now() };

// ---------------------------------------------------------------- UI state (shell ↔ lanes)

export type RouteName = "lock" | "home" | "items" | "search" | "item" | "settings";

export interface Route {
  name: RouteName;
  /** For `item`: the item id. In memory only: the URL carries an opaque token (shell/router.ts). */
  itemId?: string;
  /** For `items`: category filter, or special lists. */
  list?: CategoryId | "favorites" | "archive" | "deleted" | "all";
}

export type SortKey = "title" | "modified" | "frequency";

export type HomeSectionId = "favorites" | "recent" | "pinned";

export interface HomePrefs {
  /** Order and visibility of the Home sections (1Password "Customize"). */
  sections: { id: HomeSectionId; visible: boolean }[];
}

export type AutoLockSetting = 0 | 60 | 300 | 600;

export type Sheet =
  | { kind: "switcher" }
  | { kind: "search-scope" }
  | { kind: "sort" }
  | { kind: "categories" }
  | { kind: "field-menu"; itemId: string; fieldId: string }
  | { kind: "item-menu"; itemId: string }
  | { kind: "customize-home" }
  | { kind: "autolock" };

export type LockPhase = "locked" | "prompting" | "cancelled" | "unavailable" | "unlocked";

/** A reference to one field of one item: `${itemId}#${fieldId}`. */
export type FieldRef = string;
export const fieldRef = (itemId: string, fieldId: string): FieldRef => `${itemId}#${fieldId}`;
export function splitFieldRef(ref: FieldRef): { itemId: string; fieldId: string } {
  const i = ref.lastIndexOf("#");
  return { itemId: ref.slice(0, i), fieldId: ref.slice(i + 1) };
}

/**
 * The whole UI state. The shell (L1) owns the one mutable copy. Screens receive it read-only
 * and change it only through `AppActions`.
 */
export interface AppState {
  route: Route;
  history: Route[];
  phase: LockPhase;
  /** Which data source is wired in. Known before unlock, so the lock screen can say FAKE DATA. */
  dataSource: VaultDataSource["kind"];
  /**
   * Set when a load for the current unlock session finishes, and set back to null on every lock
   * path (Lock Now, auto-lock on return, "Immediately" as the page hides). A load that finishes
   * after a lock is discarded. Setting it to null drops the app's reference only: JavaScript
   * cannot erase memory, so when the values are actually freed is the garbage collector's call,
   * and a value already copied stays on the system clipboard until the next tap in the app
   * clears it. In the J-3704 build this holds fixture data, so nothing in it was decrypted.
   */
  snapshot: VaultSnapshot | null;
  loadError: string | null;
  /** Switcher selection (1Password account/collection): null = all wallets. */
  scope: WalletId | null;
  sort: SortKey;
  searchText: string;
  sheet: Sheet | null;
  /** Fields currently revealed, each with its auto-hide deadline (ms epoch). */
  revealed: Record<FieldRef, number>;
  largeType: FieldRef | null;
  /** Non-null while a copied value is still on the clipboard (01 §3: clear on next tap). */
  clipboard: { label: string; since: number } | null;
  toast: string | null;
  /** Up while the page is hidden, so the app-switcher snapshot shows no values. */
  privacyCover: boolean;
  /** Local favorite toggles over the replica's `favorite` flag. A UI preference, not vault data. */
  favoriteOverrides: Record<string, boolean>;
  pinned: FieldRef[];
  homePrefs: HomePrefs;
  autoLock: AutoLockSetting;
  unlockedAt: number | null;
  unlockMethod: UnlockMethod;
  online: boolean;
  version: string;
  /** Render clock (ms epoch): OTP countdowns and replica age read this, never Date.now(). */
  now: number;
}

export interface AppActions {
  navigate(route: Route): void;
  back(): void;
  openSheet(sheet: Sheet): void;
  closeSheet(): void;
  setScope(wallet: WalletId | null): void;
  setSort(sort: SortKey): void;
  setSearchText(text: string): void;
  /** Search scope pill: tick/untick a wallet; rewrites the `w:` token in the field (D-8). */
  toggleSearchWallet(wallet: WalletId): void;
  /** Call only from a user-gesture handler (WebKit async clipboard). */
  copyField(ref: FieldRef): void;
  clearClipboard(): void;
  toggleReveal(ref: FieldRef): void;
  showLargeType(ref: FieldRef): void;
  closeLargeType(): void;
  togglePin(ref: FieldRef): void;
  toggleFavorite(itemId: string): void;
  setHomePrefs(prefs: HomePrefs): void;
  /** Call only from a user-gesture handler (WebAuthn needs one). */
  unlock(): void;
  lock(): void;
  setAutoLock(setting: AutoLockSetting): void;
}
